import React from "react";
import { FaCheck } from "../../utils/icons";
import { PreviewGalleryView, type TileState } from "../PreviewGalleryView";
import { CaseReportDownloadButton } from "../CaseReportDownloadButton";
import type { GalleryFinding } from "../../hooks/useStudyQueries";

/**
 * Detail / ready state (LU-A10): shows a selected (or just-processed) upload's
 * preview. Reuses the worklist's `PreviewGalleryView` verbatim so the upload
 * detail is *identical* to the worklist preview — mono header, informational
 * badge, "Open report", and the square findings-tile grid.
 *
 * The logic lane feeds `findings` + `tileStateByCondition` (same source the
 * worklist uses via `PreviewGallery`/`ActiveCaseStage`). `justProcessed` shows a
 * one-time "Processed" confirmation banner above the preview.
 */
interface UploadDetailPanelProps {
  studyId: string;
  findings: GalleryFinding[];
  tileStateByCondition: Record<string, TileState>;
  meta?: string;
  onOpenReport: () => void;
  onSelectFinding?: (finding: GalleryFinding) => void;
  justProcessed?: boolean;
}

export const UploadDetailPanel: React.FC<UploadDetailPanelProps> = ({
  studyId,
  findings,
  tileStateByCondition,
  meta,
  onOpenReport,
  onSelectFinding,
  justProcessed = false,
}) => (
  <div data-testid="upload-detail" className="flex min-h-0 flex-1 flex-col">
    {justProcessed && (
      <div className="mb-4 flex items-center gap-2.5 rounded border border-signal-stroke-soft bg-signal-ambient px-3.5 py-2.5 text-[12.5px]">
        <FaCheck className="h-3.5 w-3.5 flex-none text-signal-text-tint" />
        <span>
          <span className="font-semibold text-fg">Processed.</span> Segmentation and AI findings are
          in.
        </span>
      </div>
    )}
    <div className="mb-3 flex justify-end">
      <CaseReportDownloadButton studyId={studyId} />
    </div>
    <PreviewGalleryView
      studyId={studyId}
      findings={findings}
      tileStateByCondition={tileStateByCondition}
      meta={meta}
      onOpenReport={onOpenReport}
      onSelectFinding={onSelectFinding}
    />
  </div>
);
